import { useState } from 'react';
import { motion } from 'framer-motion';
import { Trash2, Eye, EyeOff } from 'lucide-react';
import { SEO } from '@/components/common/SEO';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { PageLoader } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/common/EmptyState';
import { usePages, useDeletePage, useUpdatePage } from '@/hooks/usePages';
import { useTranslation } from '@/features/i18n';
import type { Page } from '@/types/database';

export function PagesAdminPage() {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [pageToDelete, setPageToDelete] = useState<Page | null>(null);

  const { data: pages, isLoading } = usePages({ includeUnpublished: true });
  const deletePage = useDeletePage();
  const updatePage = useUpdatePage();
  const { t } = useTranslation();

  const handleDelete = async () => {
    if (pageToDelete) {
      await deletePage.mutateAsync(pageToDelete.id);
      setDeleteModalOpen(false);
      setPageToDelete(null);
    }
  };

  const handleTogglePublish = async (page: Page) => {
    await updatePage.mutateAsync({
      id: page.id,
      status: page.status === 'published' ? 'draft' : 'published',
    });
  };

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <>
      <SEO title={t.admin.pages.title} noIndex />

      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">{t.admin.pages.title}</h1>
            <p className="text-neutral-600 dark:text-neutral-400">
              {t.admin.pages.subtitle}
            </p>
          </div>
        </div>

        {pages && pages.length > 0 ? (
          <div className="space-y-3 sm:space-y-4">
            {pages.map((page, index) => (
              <motion.div
                key={page.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
              >
                <Card className="p-3 sm:p-4 lg:p-5">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1">
                        <h3 className="font-semibold text-neutral-900 dark:text-white text-sm sm:text-base">
                          {page.title}
                        </h3>
                        <Badge variant={page.status === 'published' ? 'success' : 'warning'} className="text-xs">
                          {page.status === 'published' ? t.admin.common.published : t.admin.common.draft}
                        </Badge>
                      </div>
                      <p className="text-xs sm:text-sm text-neutral-600 dark:text-neutral-400 truncate">
                        /{page.slug}
                      </p>
                    </div>

                    <div className="flex items-center justify-end gap-1 sm:gap-2 flex-shrink-0 border-t sm:border-t-0 pt-2 sm:pt-0 -mx-3 sm:mx-0 px-3 sm:px-0 mt-2 sm:mt-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTogglePublish(page)}
                        title={page.status === 'published' ? t.admin.common.unpublish : t.admin.common.publish}
                        className="p-2"
                      >
                        {page.status === 'published' ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-2"
                        onClick={() => {
                          setPageToDelete(page);
                          setDeleteModalOpen(true);
                        }}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        ) : (
          <EmptyState
            title={t.admin.pages.noPages}
            description={t.admin.pages.noPagesDesc}
          />
        )}
      </div>

      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title={t.admin.pages.deletePage}
      >
        <div className="p-6">
          <p className="text-neutral-600 dark:text-neutral-400 mb-6">
            {t.admin.common.confirmDelete.replace('{name}', pageToDelete?.title || '')}
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              {t.common.cancel}
            </Button>
            <Button variant="danger" onClick={handleDelete} isLoading={deletePage.isPending}>
              {t.common.delete}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
